import { defineAsyncComponent } from 'vue';
import * as d3Selection from 'd3-selection';
import * as d3 from 'd3';
import D3Tooltip from '~/utils/d3_tooltip';

const MINIMUM_SECTION_VALUE = 0.08;
const NEXT_BARS_AMOUNT = 40;
const POPOVER_ARROW_SIZE = { height: 10, width: 20 };

export default {
  components: {
    AppChartPopover: defineAsyncComponent(() => import('~/components/AppChartPopover.vue')),
  },
  data: () => ({
    chart: null,
    svg: null,
    inner: null,
    x: null,
    x0: null,
    x1: null,
    y: null,
    bars: null,
    color: null,
    width: 600,
    height: 130,
    resizeObserver: null,
    tooltips: [],
    labelTooltips: [],
    loading: true,
    currentColumnData: {},
    popoverPosition: { x: 0, y: 0 },
    isSafari: false,
    maxValue: null,
    numberOfBarsToDisplay: 0,
    initialBuildEmpty: false,
    loadingMore: false,
    popoverOffset: { left: 0, top: 0, right: 0, bottom: 0 },
    hidePopoverTimeout: null,
    POPOVER_ARROW_SIZE,
  }),
  props: {
    chartId: {
      type: String,
      default: 'default',
    },
    chartData: {
      type: Array,
      required: true,
    },
    margin: {
      type: Object,
      default: () => ({
        top: 20,
        right: 0,
        bottom: 15,
        left: 0,
      }),
    },
    columnSpacing: {
      type: Number,
      default: 0.25,
    },
    paddingOuter: {
      type: Number,
      default: 0,
    },
    yTickFormat: {
      type: String,
      default: '',
    },
    hasYAxis: {
      type: Boolean,
      default: false,
    },
    hasXAxis: {
      type: Boolean,
      default: true,
    },
    sections: {
      type: Array,
      required: true,
    },
    forcedAmountOfBars: {
      type: Number,
      default: 0,
    },
    colorRange: {
      type: Array,
      default: () => ['#2aa7ff', '#6b7280', '#f98080'],
    },
    tooltipTemplate: {
      type: String,
      default:
        '<div class="vue-tooltip top-start chart-tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
    },
    labelEllipsis: {
      type: Number,
      default: 2,
    },
    dynamicHeight: {
      type: Boolean,
      default: false,
    },
    yAxisWidth: {
      type: Number,
      default: 30,
    },
    barRadius: {
      type: Number,
      default: 4,
    },
    fixedWidthBars: {
      type: Number,
      default: 0,
    },
    fixedHeight: {
      type: Number,
      default: 0,
    },
    showPopovers: {
      type: Boolean,
      default: false,
    },
    popoverOriginPosition: {
      type: String,
      default: 'chart',
      validator: (value) => ['chart', 'column'].includes(value),
    },
    popoverTrigger: {
      type: String,
      default: 'mouseover',
      validator: (value) => ['mouseover', 'click'].includes(value),
    },
  },
  watch: {
    formattedChartData() {
      if (this.loadingMore && this.svg) {
        this.updateChart();
        this.loadingMore = false;
      }
    },
    // In Vue 3 the widget can mount before its data lands in the store (the loading flag isn't
    // set yet on first render), so the initial build runs empty and every scale/axis is zeroed.
    // When data arrives after that empty mount, wipe the svg and rebuild from scratch — partial
    // redraws (updateChart alone) keep the stale Y scale. resizeChart must run explicitly: it is
    // what writes the section paths' `d` (drawBars), and Firefox does not re-fire the
    // ResizeObserver after the rebuild the way Chrome does.
    chartData() {
      if (this.svg && !this.loadingMore && this.initialBuildEmpty && this.chartData.length) {
        this.initialBuildEmpty = false;
        this.svg.selectAll('*').remove();
        this.calculateInitialBars();
        this.buildChart();
        this.resizeChart();
      }
    },
  },
  mounted() {
    this.isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    this.$nextTick(() => {
      this.calculateInitialBars();
      this.buildChart();
      this.initialBuildEmpty = !this.chartData.length;
      this.setupResizeObserver();
      document.querySelector(`.svg-wrapper-${this.chartId}`)?.addEventListener('scroll', this.hidePopover);
    });

    this.$refs.chartArea.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    this.cleanupTooltips();
    this.cleanupLabelTooltips();
    this.stopResizeObserver();
    document.querySelector(`.svg-wrapper-${this.chartId}`)?.removeEventListener('scroll', this.hidePopover);

    this.$refs.chartArea.removeEventListener('scroll', this.handleScroll);
  },
  computed: {
    moreBarsToLoad() {
      return this.numberOfBarsToDisplay < this.chartData.length;
    },
    formattedChartData() {
      return this.chartData.slice(0, this.numberOfBarsToDisplay).map((column, index) => {
        const vColumn = [];
        // Setting the correct structure to generate the chart
        // Also calculating the y values of each section so that they can be stacked
        const details = this.sections
          .map((name) => {
            let columnValue = column[name].value;

            if (column[name].value < MINIMUM_SECTION_VALUE) {
              columnValue = MINIMUM_SECTION_VALUE;
            }
            const vBegin = vColumn.reduce((total, current) => total + current, 0) || 0;
            const vEnd = +columnValue * column.value + vBegin;

            vColumn.push(+column[name].value * column.value);

            return { ...column[name], name, vBegin, vEnd };
          })
          .filter((column) => column.value);
        const total = d3.max(details, (column) => column.vEnd);

        return { label: column.label, details, total, index };
      });
    },
    mappedColors() {
      return this.sections.reduce((acc, key, index) => {
        acc[key] = this.colorRange[index];

        return acc;
      }, {});
    },
    hasPopoverData() {
      return this.currentColumnData.title || this.currentColumnData.content;
    },
    extraPadding() {
      return this.isSafari && this.hasYAxis ? { 'padding-left': `${this.yAxisWidth}px` } : {};
    },
    yDomainHeight() {
      return this.chartData.reduce((maxHeight, column) => {
        const yColumn = this.sections.reduce((total, name) => {
          let columnValue = column[name].value;

          if (columnValue < MINIMUM_SECTION_VALUE) {
            columnValue = MINIMUM_SECTION_VALUE;
          }

          return total + columnValue * column.value;
        }, 0);

        return Math.max(maxHeight, yColumn);
      }, 0);
    },
    popoverOriginIsColumn() {
      return this.popoverOriginPosition === 'column';
    },
    popoverTriggerIsClick() {
      return this.popoverTrigger === 'click';
    },
    hasDialogParent() {
      const chart = document.querySelector(`#${this.chartId}`);

      return Boolean(chart?.closest('.app-dialog')) || false;
    },
  },
  methods: {
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.resizeChart);
      });

      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    // TOOLTIPS
    generateTooltipContent(tooltipData) {
      if (!tooltipData) {
        return '';
      }
      // Custom HTML to be possible to have <ul> elements in the tooltip content
      // Depending on the data object we can have plain text or a list.
      // If typeof tooltipData === 'object' we expect schema like
      // {
      //    name: string,
      //    total: number,
      //    items: [
      //      {
      //        label: string,
      //        value: number
      //      }
      //    ]
      //  }
      // Otherwise, we just render a string.
      if (typeof tooltipData === 'object' && tooltipData.items?.length) {
        const list = tooltipData.items.length
          ? `<ul class="pl-20 m-0 text-left">
            ${tooltipData.items
              .map((item) => `<li>${item.label}: ${Math.round(item.value * 1000) / 10}%</li>`)
              .join('')}
          </ul>`
          : '';

        return `<span>${tooltipData.name}</span>${list}`;
      }

      if (typeof tooltipData === 'string') {
        return `<span>${tooltipData}</span>`;
      }
    },
    cleanupTooltips() {
      if (this.tooltips.length) {
        // Cleaning tooltips to avoid accumulating when resizing window
        this.tooltips.forEach((tooltip) => tooltip.dispose());
        this.tooltips.splice(0);
      }
    },
    cleanupLabelTooltips() {
      this.labelTooltips.forEach((tooltip) => tooltip.dispose());
      this.labelTooltips.splice(0);
    },
    // POPOVERS
    popoverBarWidth(data, isOrigin = false) {
      if (this.isVertical) {
        return this.fixedWidthBars || this.x1.bandwidth();
      }

      return this.popoverOriginIsColumn && isOrigin ? this.x(0) - this.x(data.total) : this.width;
    },
    popoverBarHeight(data, isOrigin = false) {
      if (this.isVertical) {
        return this.popoverOriginIsColumn && isOrigin ? this.y(0) - this.y(data.total) : this.height;
      }

      return this.fixedWidthBars || this.x1.bandwidth();
    },
    generatePopovers(that, enterBar) {
      if (that.showPopovers) {
        enterBar
          .append('rect')
          // Used as the origin of the popover - either the filled bar or the total possible height it can have
          .attr('class', (d) => `y-column y-column-${d.index}-${this.chartId}`)
          .style('opacity', 0)
          .attr('width', (d) => this.popoverBarWidth(d, true))
          .attr('height', (d) => this.popoverBarHeight(d, true))
          .attr('y', (d) => (this.popoverOriginIsColumn ? this.y(d.total) : 0));

        enterBar
          .append('rect')
          // Hoverable element which will trigger the popover
          .attr('class', `y-column-selector y-column-selector-${this.chartId}`)
          .attr('width', (d) => this.popoverBarWidth(d))
          .attr('height', (d) => this.popoverBarHeight(d))
          .style('opacity', 0)
          .on(that.popoverTrigger, function (_event, d) {
            const i = d.index;
            const data = that.chartData[i];

            clearTimeout(that.hidePopoverTimeout);
            const svgEl = document.querySelector(`.svg-wrapper-${that.chartId}`);
            const columnEl = document.querySelector(`.y-column-${d.index}-${that.chartId}`);
            const bounds =
              that.popoverOriginPosition === 'column'
                ? columnEl?.getBoundingClientRect()
                : this.getBoundingClientRect();
            // Getting the top of the topmost filled portion of the column
            const barSections = svgEl?.querySelectorAll('.bar')[i].getElementsByClassName('section') || [];
            const barSectionsArray = [].slice.call(barSections);
            const sectionsBoundingClientRects = barSectionsArray.map((section) => section.getBoundingClientRect());
            const topMostSection = sectionsBoundingClientRects.reduce(
              (acc, curr) => (curr.top < acc ? curr.top : acc),
              bounds.bottom,
            );
            // Adding the height of all filled portions of the column
            const totalHeight = sectionsBoundingClientRects.reduce((acc, curr) => curr.height + acc, 0);
            const content = {
              ...data,
              title: data.main || data.title || data.label,
              id: data.id || null,
              total: d.total,
              details: d.details,
              colors: that.mappedColors,
              avatar_url: data.avatar_url,
            };

            // x = distance between column and left of page + half the column's width
            // y = distance between topmost filled portion of the column and top of the page + scrolled distance of the page
            that.showPopover(content, bounds.left + bounds.width / 2, topMostSection + window.scrollY, totalHeight);
          })
          .on('mouseout', function (event) {
            if (that.popoverTrigger !== 'click') {
              if (event.relatedTarget?.className !== 'chart-popover') {
                that.triggerHidePopover();
              }
            }
          });
      }
    },
    showPopover(content, x, y, chartBarHeight) {
      this.currentColumnData = content;
      this.popoverPosition.x = x;
      this.popoverPosition.y = y;
      this.popoverPosition.bottomY = y + chartBarHeight; // bottom of the column, used if there's no space in the page above filled portion of the column
      this.$refs[`chart-popover-${this.chartId}`].focus();
      this.$emit('popover-open', content);
    },
    hidePopover() {
      this.currentColumnData = {};
    },
    // CHART
    setChartColors() {
      // Colors used in the Chart
      this.color = d3.scaleOrdinal().range(this.colorRange);
      this.color.domain(this.sections);
    },
    setChartSizing() {
      this.height = this.chartHeight;
      this.width = this.$el.clientWidth - (this.hasYAxis ? this.yAxisWidth : 0);

      this.svg
        .attr('class', 'chart-svg')
        .attr('width', this.width + this.margin.left + this.margin.right)
        .attr('height', this.height + this.margin.top + this.margin.bottom)
        .attr('transform', `translate(${this.hasYAxis ? this.yAxisWidth : 0}, 0)`);
    },
    createBars() {
      const that = this;
      const barGroups = this.svg.selectAll('.bar').data(this.formattedChartData, (d, i) => `${d.label}__${i}`);

      barGroups.join(
        (enter) =>
          enter
            .append('g')
            .attr('class', 'bar')
            .attr('transform', (d, i) => {
              if (this.isVertical) {
                const xTranslation = this.x0(`${d.label}__${i}`) || 0;

                if (xTranslation === undefined) {
                  return 'translate(0, 0)';
                }

                return `translate(${xTranslation}, 0)`;
              }
              const columnLabelYTransform = this.y(`${d.label}__${i}`) || 0;

              return `translate(0, ${columnLabelYTransform})`;
            })
            .call((enterBar) => {
              enterBar
                .selectAll('.section')
                .data((d) => [...d.details].reverse())
                .join('path')
                .attr('class', (section) => `section section-${section.name}`)
                .attr('fill', (section) => that.color(section.name))
                .on('mouseover', function (_event, data) {
                  that.$emit('mouseover-chart');
                  // Tooltip logic
                  that.cleanupTooltips();

                  const tooltip = new D3Tooltip(this, that.generateTooltipContent(data.tooltip), {
                    html: true,
                    template: that.tooltipTemplate,
                  });

                  that.tooltips.push(tooltip);
                })
                .on('mouseout', function () {
                  that.$emit('mouseover-chart');
                  // Tooltip cleanup logic
                  that.cleanupTooltips();
                });

              that.generatePopovers(that, enterBar);
            }),
        (update) =>
          update.attr('transform', (d) => {
            const xTranslation = this.x0(d.label);

            if (xTranslation === undefined) {
              return 'translate(0, 0)';
            }

            return `translate(${xTranslation}, 0)`;
          }),
      );

      barGroups.exit().remove();
    },
    drawBars() {
      this.svg.selectAll('.bar').each((d, i, nodes) => {
        const bar = d3.select(nodes[i]);

        bar.selectAll('.section').attr('d', (section, index) => {
          const radius = index === 0 ? this.barRadius : 0;

          return this.createBarSection(section, radius);
        });
      });

      // Manage the empty bars
      this.svg.selectAll('.bar > .section-empty').attr('d', (section) => {
        return this.createBarSection(section, 0, true);
      });

      const barGroups = this.svg.selectAll('.bar');

      // If chart with popovers, update hoverable element's sizing
      if (this.showPopovers) {
        if (this.isVertical) {
          barGroups.selectAll('.y-column').attr('width', this.fixedWidthBars || this.x1.bandwidth());
          barGroups
            .selectAll(`.y-column-selector-${this.chartId}`)
            .attr('width', (d) => this.popoverBarWidth(d), true)
            .attr('height', (d) => this.popoverBarHeight(d), true);
        } else {
          barGroups.selectAll('.y-column').attr('height', this.fixedWidthBars || this.x1.bandwidth());
          barGroups
            .selectAll(`.y-column-selector-${this.chartId}`)
            .attr('height', (d) => this.popoverBarHeight(d))
            .attr('width', (d) => this.popoverBarWidth(d));
        }
      }
    },
    // LABELS
    labelCustomization(label, width) {
      // Add responsiveness and tooltips to each individual bar label
      const that = this;

      this.cleanupTooltips();

      label.each(function (d, i) {
        const fullText = that.chartData[i].main;
        const text = d3.select(this);
        const labelText = text.text().replace(/__\d+$/, '');

        // Wrap text when overflowing space for each label
        that.handleLabelWrap(text, labelText, width);

        // If the label needs an ellipsis, create a tooltip to show the full text on hover
        const hasEllipsis = that.handleEllipsis(text, width, labelText);

        if (hasEllipsis || labelText !== fullText) {
          const elTooltip = d3Selection.local();
          const toolTipValue = hasEllipsis ? labelText : fullText;

          if (!this.elTooltip?.get(this)) {
            const tooltip = new D3Tooltip(this, toolTipValue);

            elTooltip.set(this, tooltip);
            that.labelTooltips.push(tooltip);
          }
        }
      });
    },
    handleLabelWrap(text, label, width) {
      const words = label.split(/\s+/).reverse();
      const lineHeight = 1;
      const y = text.attr('y');
      const dy = parseFloat(text.attr('dy'));
      let word;
      let line = [];
      let lineNumber = 0;
      let tspan = text
        .text(null)
        .append('tspan')
        .attr('x', 0)
        .attr('y', y)
        .attr('dy', dy + 'em');

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));
        // Check if line overflows adding current loop word and creating new tspan if that's the case
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = text
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + dy + 'em')
            // Hides tspans after ellipsis lines, since it will be truncated
            .attr('style', `opacity: ${lineNumber + 1 > this.labelEllipsis ? 0 : 1}`)
            .text(word);
        }
      }
    },
    handleEllipsis(text, width, labelText) {
      // Get total lines of label after wrapping text and get child to truncate
      const totalChildren = text.node().children?.length;
      const targetChild = totalChildren > this.labelEllipsis - 1 && text.node().children[this.labelEllipsis - 1];
      let textLength = targetChild && targetChild.getComputedTextLength();
      let textContent = targetChild.textContent;
      const ellipsis = textLength > width || totalChildren > this.labelEllipsis;

      // Check if line needs to be truncated due to overflow or more children existing
      if (textLength > width) {
        while (textLength > width && textContent.length) {
          textContent = this.truncateString(targetChild, textContent, labelText);
          textLength = targetChild.getComputedTextLength();
        }
      } else if (totalChildren > this.labelEllipsis) {
        textContent = this.truncateString(targetChild, textContent, labelText);
      }

      return ellipsis;
    },
    truncateString(item, string, initial) {
      string = string.slice(0, -1);
      item.textContent = string ? `${string}...` : initial[0];

      return string;
    },
    handleScroll(event) {
      if (this.loadingMore) {
        return;
      }

      const { scrollLeft, clientWidth, scrollWidth } = event.target;

      if (scrollLeft + clientWidth >= scrollWidth - 100) {
        if (this.moreBarsToLoad) {
          this.loadingMore = true;
          this.numberOfBarsToDisplay += NEXT_BARS_AMOUNT;
        }
      }
    },
    calculateInitialBars() {
      if (this.forcedAmountOfBars) {
        this.numberOfBarsToDisplay = this.forcedAmountOfBars;

        return;
      }
      if (this.fixedWidthBars && this.isVertical) {
        this.numberOfBarsToDisplay = Math.floor(
          this.$refs.chartArea.getBoundingClientRect().width / (this.fixedWidthBars * 2) + 5,
        );
      } else {
        this.numberOfBarsToDisplay = this.chartData.length;
      }
    },
    setPopoverOffset() {
      const topbar = document.querySelector('.top-bar')?.getBoundingClientRect();
      const sidebar = document.querySelector('.nav-sidebar')?.getBoundingClientRect();

      this.popoverOffset = {
        left: sidebar?.width || 0,
        top: topbar?.height || 0,
        right: 20,
        bottom: 0,
      };
    },
    triggerHidePopover() {
      this.hidePopoverTimeout = setTimeout(() => {
        this.hidePopover();
      }, 10);
    },
    popoverFocusOut() {
      if (this.popoverTriggerIsClick) {
        this.triggerHidePopover();
      }
    },
  },
};
